export interface CallAuditNote {
  ID: number;
  callID: string;
  type: CallAuditNoteType;
  message: string;
  timestamp: Date;
}

export interface CallAuditNoteResponse {
  ID: number;
  callID: string;
  type: CallAuditNoteType;
  message: string;
  timestamp: string;
}

export interface CallAuditActivation {
  activationID: number;
  noteCount: number;
  notes: CallAuditNote[];
}

export interface CallAuditActivationResponse {
  activationID: number;
  noteCount: number;
  notes: CallAuditNoteResponse[];
}

export interface CallAuditLog {
  patientRecordID: number;
  totalNotes: number;
  activations: CallAuditActivation[];
}

export interface CallAuditLogResponse {
  patientRecordID: number;
  totalNotes: number;
  activations: CallAuditActivationResponse[];
}

export enum CallAuditNoteType {
  INFO = "INFO",
  WARNING = "WARNING",
  ERROR = "ERROR"
}

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ValidationError";
  }
}

export class CallAuditLogValidationError extends ValidationError {
  field: string;

  constructor(message: string, field: string) {
    super(message);
    this.name = "CallAuditLogValidationError";
    this.field = field;
  }
}

export class CallAuditNoteModel implements CallAuditNote {
  ID: number;
  callID: string;
  type: CallAuditNoteType;
  message: string;
  timestamp: Date;

  constructor(data: CallAuditNoteResponse) {
    this.ID = data.ID;
    this.callID = data.callID;
    this.type = data.type;
    this.message = data.message;
    this.timestamp = new Date(data.timestamp);
  }

  static validate(data: any): data is CallAuditNoteResponse {
    if (typeof data.ID !== "number") {
      throw new CallAuditLogValidationError("ID must be a number", "ID");
    } else if (typeof data.callID !== "string") {
      throw new CallAuditLogValidationError("callID must be a string", "callID");
    } else if (!Object.values(CallAuditNoteType).includes(data.type)) {
      throw new CallAuditLogValidationError("type must be a valid CallAuditNoteType", "type");
    } else if (typeof data.message !== "string") {
      throw new CallAuditLogValidationError("message must be a string", "message");
    } else if (typeof data.timestamp !== "string" || new Date(data.timestamp).getTime() === 0) {
      throw new CallAuditLogValidationError("timestamp must be a valid Date string", "timestamp");
    } else {
      return true;
    }
  }
}

export class CallAuditActivationModel implements CallAuditActivation {
  activationID: number;
  noteCount: number;
  notes: CallAuditNote[];

  constructor(data: CallAuditActivationResponse) {
    this.activationID = data.activationID;
    this.noteCount = data.noteCount;
    this.notes = data.notes.map(note => new CallAuditNoteModel(note));
  }

  static validate(data: any): data is CallAuditActivationResponse {
    if (typeof data.activationID !== "number") {
      throw new CallAuditLogValidationError("activationID must be a number", "activationID");
    } else if (typeof data.noteCount !== "number") {
      throw new CallAuditLogValidationError("noteCount must be a number", "noteCount");
    } else if (!Array.isArray(data.notes)) {
      throw new CallAuditLogValidationError("notes must be an array", "notes");
    } else {
      data.notes.forEach((note: any, index: number) => {
        try {
          CallAuditNoteModel.validate(note);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          throw new CallAuditLogValidationError(`Invalid note at index ${index}: ${errorMessage}`, `notes[${index}]`);
        }
      });
      return true;
    }
  }
}

export class CallAuditLogModel implements CallAuditLog {
  patientRecordID: number;
  totalNotes: number;
  activations: CallAuditActivation[];

  constructor(data: CallAuditLogResponse) {
    this.patientRecordID = data.patientRecordID;
    this.totalNotes = data.totalNotes;
    this.activations = data.activations.map(activation => new CallAuditActivationModel(activation));
  }

  static validate(data: any): data is CallAuditLogResponse {
    if (typeof data.patientRecordID !== "number") {
      throw new CallAuditLogValidationError("patientRecordID must be a number", "patientRecordID");
    } else if (typeof data.totalNotes !== "number") {
      throw new CallAuditLogValidationError("totalNotes must be a number", "totalNotes");
    } else if (!Array.isArray(data.activations)) {
      throw new CallAuditLogValidationError("activations must be an array", "activations");
    } else {
      data.activations.forEach((activation: any, index: number) => {
        try {
          CallAuditActivationModel.validate(activation);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          throw new CallAuditLogValidationError(`Invalid activation at index ${index}: ${errorMessage}`, `activations[${index}]`);
        }
      });
      return true;
    }
  }
}